import React, {useReducer, forwardRef, useImperativeHandle} from 'react'

import {Alert, Button, Modal, ModalActions, Row} from '../UI/index.js'
import Address from '../addresses/Address.js'
import {Select} from '../UI/forms/index.js'
import t from '../../helpers/Languages.js'

import {Reseller, Branch} from '../../../server/functions/resellers/resellers.types.js'

interface EditBranchModalProps {
    reseller: Reseller
    onChange: (branch: Branch) => void
}

interface EditBranchModalState {
    modalIsOpen: boolean
    branch?: Branch
    error?: string
    validation: {
        name: boolean
    }
}

export interface EditBranchModalRef {
    open: (branch: Branch) => void
}

const EditBranchModal = forwardRef<EditBranchModalRef, EditBranchModalProps>((props, ref) => {
    const {reseller, onChange} = props

    const initialState: EditBranchModalState = {
        modalIsOpen: false,
        branch: undefined,
        error: undefined,
        validation: {
            name: false
        }
    }

    const [state, setState] = useReducer((state: EditBranchModalState, newState: Partial<EditBranchModalState>) => ({...state, ...newState}), structuredClone(initialState))

    useImperativeHandle(ref, () => ({open}))

    const open = (branch: Branch) => {
        if (!branch?.name) {
            setTimeout(() => {
                document.getElementById('nameBranch')?.focus()
            }, 10)
        }
        setState({modalIsOpen: true, branch})
    }

    const close = () => {
        setState(initialState)
    }

    const save = () => {
        if (!validate()) {
            return
        }
        if (state.branch) {
            onChange(state.branch)
        }
        close()
    }

    const validate = () => {
        let valid = true
        let focusId = ''
        let error = ''
        let validation = {name: false}

        if (!state.branch?.name) {
            valid = false
            error = t('errorsNameRequired')
            focusId = 'nameBranch'
            validation.name = true
        }

        setState({error, validation})

        if (!valid && focusId) {
            document.getElementById(focusId)?.focus()
        }

        return valid
    }

    return (
        <Modal
            style={{maxWidth: 800}}
            show={state.modalIsOpen}
            title={reseller.settings.branches?.some((branch) => branch.id === state.branch?.id) ? t('editBranch') : t('newBranch')}
            onClose={close}
            closeButton
        >
            <Row>
                <Address
                    id='Branch'
                    address={state.branch?.address}
                    onChange={(address) => {
                        setState({branch: {...state.branch!, address, name: address.name}})
                    }}
                    isInvalid={state.validation.name}
                />
            </Row>

            <Row style={{marginTop: 12, marginBottom: 12}}>
                <Select
                    label={t('deliveryZones')}
                    info={t('branchDeliveryZonesInfo')}
                    options={(reseller.settings.deliveryZones || []).map((deliveryZone) => ({value: deliveryZone.id, title: deliveryZone.name}))}
                    values={state.branch?.deliveryZoneIds || []}
                    onChange={(event) => {
                        setState({branch: {...state.branch!, deliveryZoneIds: event.target.values}})
                    }}
                />
            </Row>

            {state.error && <Alert style={{marginBottom: 6}} variant='danger'>{state.error}</Alert>}

            <ModalActions style={{marginTop: 12}}>
                <Button
                    style={{marginTop: 24}}
                    onClick={save}
                    label={t('save')}
                />
            </ModalActions>
        </Modal>
    )
})

EditBranchModal.displayName = 'EditBranchModal'

export default EditBranchModal
